import React from 'react';

/**
 * The optional recurring redemption window on a venue reward (SSA-1328).
 *
 * A reward with no window is redeemable any time — that is what every existing
 * reward holds, so the control stays off until an admin turns it on. Turning it
 * on requires all three values; the API and the V143 CHECK constraint both
 * reject a partial window.
 *
 * Days use EXTRACT(dow) numbering, 0 = Sunday, matching the migration and the
 * business-app API. That differs from the string day names deals use
 * ("mon", "tue") — the two features have separate contracts, and matching the
 * reward API is what keeps a reward authored here identical to one authored
 * from the business app.
 */

/** Index IS the value: 0 = Sunday .. 6 = Saturday. */
const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export interface RewardWindowValue {
  days: number[];
  start: string;  // "HH:MM"
  end: string;    // "HH:MM"
}

export const EMPTY_WINDOW: RewardWindowValue = { days: [], start: '17:00', end: '23:00' };

/** True when the window is complete enough to send. */
export function windowIsComplete(v: RewardWindowValue): boolean {
  return v.days.length > 0 && Boolean(v.start) && Boolean(v.end) && v.start !== v.end;
}

/** Why the window can't be saved yet, or null when it's fine. */
export function windowError(enabled: boolean, v: RewardWindowValue): string | null {
  if (!enabled) return null;
  if (v.days.length === 0) return 'Pick at least one day.';
  if (!v.start || !v.end) return 'Set both a start and an end time.';
  // Mirrors the API's redeem_window_zero_length: end before start is a
  // legitimate midnight wrap, but equal times would read as always-open.
  if (v.start === v.end) return 'Start and end cannot be the same time.';
  return null;
}

/** The three API fields for a create/update body. */
export function toWindowPayload(enabled: boolean, v: RewardWindowValue) {
  if (!enabled) {
    // Explicit nulls clear an existing window; on create they mean "no window".
    return { redeem_days: null, redeem_start_local: null, redeem_end_local: null };
  }
  return {
    redeem_days: [...v.days].sort((a, b) => a - b),
    redeem_start_local: v.start,
    redeem_end_local: v.end,
  };
}

/** An existing reward's stored window, back into editable form. */
export function fromReward(reward: {
  redeem_days: number[] | null;
  redeem_start_local: string | null;
  redeem_end_local: string | null;
}): { enabled: boolean; value: RewardWindowValue } {
  if (!reward.redeem_days || reward.redeem_days.length === 0) {
    return { enabled: false, value: EMPTY_WINDOW };
  }
  return {
    enabled: true,
    value: {
      days: reward.redeem_days,
      start: reward.redeem_start_local || EMPTY_WINDOW.start,
      end: reward.redeem_end_local || EMPTY_WINDOW.end,
    },
  };
}

/** Compact read-only summary, e.g. "Mon, Wed 5:00 PM–11:00 PM". */
export function windowSummary(reward: {
  redeem_days: number[] | null;
  redeem_start_local: string | null;
  redeem_end_local: string | null;
}): string | null {
  if (!reward.redeem_days || reward.redeem_days.length === 0) return null;
  const days = [...reward.redeem_days].sort((a, b) => a - b).map(d => DOW_LABELS[d] ?? '?');
  return `${days.join(', ')} ${reward.redeem_start_local}–${reward.redeem_end_local}`;
}

export function RewardWindowFields({
  enabled, value, onEnabledChange, onChange, idPrefix,
}: {
  enabled: boolean;
  value: RewardWindowValue;
  onEnabledChange: (enabled: boolean) => void;
  onChange: (value: RewardWindowValue) => void;
  idPrefix: string;
}) {
  const err = windowError(enabled, value);

  const toggleDay = (dow: number) => {
    const days = value.days.includes(dow)
      ? value.days.filter(d => d !== dow)
      : [...value.days, dow];
    onChange({ ...value, days });
  };

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => onEnabledChange(e.target.checked)}
          className="accent-violet-500"
        />
        <span className="text-xs font-semibold text-gray-300">
          Limit when this can be redeemed
        </span>
      </label>

      {!enabled && (
        <p className="text-[11px] text-gray-500">Redeemable any time.</p>
      )}

      {enabled && (
        <div className="space-y-2 pl-6">
          <div className="flex flex-wrap gap-1">
            {DOW_LABELS.map((label, dow) => {
              const on = value.days.includes(dow);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => toggleDay(dow)}
                  className={`px-2 py-1 rounded-lg border text-[11px] font-medium transition-all ${
                    on
                      ? 'bg-violet-950/50 border-violet-700 text-violet-200'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <input
              id={`${idPrefix}-start`}
              type="time"
              value={value.start}
              onChange={e => onChange({ ...value, start: e.target.value })}
              className="px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs text-white focus:outline-none focus:border-violet-500"
            />
            <span className="text-xs text-gray-500">to</span>
            <input
              id={`${idPrefix}-end`}
              type="time"
              value={value.end}
              onChange={e => onChange({ ...value, end: e.target.value })}
              className="px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs text-white focus:outline-none focus:border-violet-500"
            />
          </div>

          {/* An end before the start is a deliberate overnight window, not a
              mistake — say so, because it looks like one. */}
          {!err && value.end < value.start && (
            <p className="text-[11px] text-gray-500">
              Ends after midnight, the following day.
            </p>
          )}
          {err && <p className="text-[11px] text-amber-400">{err}</p>}
        </div>
      )}
    </div>
  );
}
